import type { TemplateLayoutSpec } from "@/types/template-layout/templateLayout.types";

const STORED_ASSET_ID_PATTERN =
  /(?:^|\/)briefing-templates\/\d+\/assets\/([0-9A-HJKMNP-TV-Z]{26})\//;

/**
 * The stored asset id in an Image src, for relative `/media/...` paths and full storage
 * URLs; null for anything else, such as a remote link.
 */
export const layoutImageAssetId = (src: string): string | null =>
  STORED_ASSET_ID_PATTERN.exec(src)?.[1] ?? null;

/** True when two srcs show the same image: the same stored asset in any URL form, or the same link. */
export const isSameLayoutImage = (src: string, other: string): boolean => {
  const assetId = layoutImageAssetId(src);
  return assetId != null
    ? assetId === layoutImageAssetId(other)
    : src === other;
};

/** Points every Image that shows `src` at `nextSrc`, keeping its other props. */
export const replaceLayoutImage = <
  TSpec extends Pick<TemplateLayoutSpec, "elements">,
>(
  spec: TSpec,
  src: string,
  nextSrc: string,
): TSpec => ({
  ...spec,
  elements: Object.fromEntries(
    Object.entries(spec.elements).map(([elementId, element]) =>
      element.type === "Image" &&
      typeof element.props?.src === "string" &&
      isSameLayoutImage(element.props.src, src)
        ? [elementId, { ...element, props: { ...element.props, src: nextSrc } }]
        : [elementId, element],
    ),
  ),
});
